import { useState } from 'react';
import { useResume } from './hooks/useResume';
import { Editor } from './components/Editor';
import { Preview } from './components/Preview';
import { UploadZone } from './components/UploadZone';
import { AiPanel } from './components/AiPanel';
import { SettingsPanel } from './components/SettingsPanel';
import { printResume } from './lib/print';
import {
  Printer,
  Layout,
  Plus,
  Upload,
  ChevronDown,
  FileText,
  Sparkles,
  Settings,
  Wand2,
  Target,
  TrendingUp,
  ShieldCheck,
  Globe,
  MessageSquare,
  Linkedin,
  ScanSearch,
  GraduationCap,
  Languages,
  ListChecks,
  ChevronRight,
  Star,
  Code2,
  Briefcase,
  Zap,
  ArrowRight,
} from 'lucide-react';

const templates = [
  { key: 'standard', label: 'Standard' },
  { key: 'modern', label: 'Modern' },
  { key: 'classic', label: 'Classic' },
  { key: 'professional', label: 'Professional' },
  { key: 'executive', label: 'Executive' },
  { key: 'minimal', label: 'Minimal' },
  { key: 'technical', label: 'Technical' },
  { key: 'portfolio', label: 'Portfolio' },
  { key: 'elegant', label: 'Elegant' },
  { key: 'compact', label: 'Compact' },
  { key: 'sidebar-right', label: 'Sidebar' },
  { key: 'ats', label: 'ATS' },
  { key: 'two-column', label: 'Two Column' },
  { key: 'academic', label: 'Academic' },
  { key: 'creative', label: 'Creative' },
  { key: 'ats-compact', label: 'ATS Compact' },
  { key: 'ats-serif', label: 'ATS Serif' },
  { key: 'corporate-blue', label: 'Corporate Blue' },
  { key: 'boardroom', label: 'Boardroom' },
  { key: 'engineer', label: 'Engineer' },
  { key: 'devops', label: 'DevOps' },
  { key: 'cybersecurity', label: 'Cybersecurity' },
  { key: 'network', label: 'Network Engineer' },
  { key: 'data', label: 'Data' },
  { key: 'cloud', label: 'Cloud' },
] as const;

type EditorPane = 'editor' | 'preview' | 'ai';

const editorPanes: { key: EditorPane; label: string; icon: typeof FileText }[] = [
  { key: 'editor', label: 'Edit', icon: FileText },
  { key: 'preview', label: 'Preview', icon: Layout },
  { key: 'ai', label: 'AI', icon: Sparkles },
];

const features = [
  {
    icon: Sparkles,
    title: 'AI Resume Analysis',
    description: 'Get an ATS readiness score with strengths, improvements, and missing keywords.',
  },
  {
    icon: Target,
    title: 'Job Tailoring',
    description: 'Paste any job description and AI adapts your resume to match the role.',
  },
  {
    icon: Wand2,
    title: 'AI Writing Tools',
    description: 'Generate cover letters, LinkedIn profiles, portfolio sites, and more.',
  },
  {
    icon: ShieldCheck,
    title: 'ATS Scanner',
    description: 'Compare your resume against a job description and surface missing keywords.',
  },
  {
    icon: Layout,
    title: '25+ Professional Templates',
    description: 'ATS-optimized, modern, and role-specific designs for every career stage.',
  },
  {
    icon: Upload,
    title: 'Upload & Parse',
    description: 'Import PDF, DOCX, DOC, TXT, or screenshots and auto-fill your resume.',
  },
];

const aiTools = [
  { icon: MessageSquare, label: 'Cover Letter' },
  { icon: Zap, label: 'Tone Shift' },
  { icon: ArrowRight, label: 'Expand / Shorten' },
  { icon: TrendingUp, label: 'Quantify Impact' },
  { icon: Briefcase, label: 'Interview Prep' },
  { icon: Linkedin, label: 'LinkedIn About' },
  { icon: Linkedin, label: 'LinkedIn Profile' },
  { icon: Globe, label: 'Portfolio Site' },
  { icon: GraduationCap, label: 'Cert Suggestions' },
  { icon: ListChecks, label: 'JD Skills' },
  { icon: Languages, label: 'Translate' },
  { icon: ScanSearch, label: 'ATS Scanner' },
];

export default function App() {
  const resume = useResume();
  const [mode, setMode] = useState<'landing' | 'editor'>('landing');
  const [exporting, setExporting] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  // Below `lg` there is not enough width for a split view, so the panes
  // become a single-pane switcher. At `lg`+ this value is ignored.
  const [pane, setPane] = useState<EditorPane>('editor');

  const handleExport = async () => {
    setExporting(true);
    try {
      printResume(resume.data, resume.template, 'standard', resume.settings);
    } catch (err: any) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (mode === 'landing') {
    return (
      <div className="resume-app">
        <div className="app-shell min-h-screen flex flex-col">
          {/* Navigation */}
          <nav className="rx-dark rx-nav sticky top-0 z-50">
            <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="rx-footer-mark" aria-hidden="true">
                  <FileText className="h-4 w-4" />
                </div>
                <div>
                  <span className="font-bold text-lg tracking-tight text-slate-900 block leading-none">
                    Resume<span className="rx-accent">.</span>Builder
                  </span>
                  <div className="rx-dots mt-1.5" aria-hidden="true">
                    <i /><i /><i /><i /><i />
                  </div>
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-3">
                <span className="rx-pill">
                  <span className="rx-pill-dot" aria-hidden="true" />
                  AI Online
                </span>
                <button onClick={() => setMode('editor')} className="bg-primary-600 text-white px-4 py-2 text-sm font-medium">
                  Open Editor
                </button>
              </div>
            </div>
          </nav>

          {/* Hero */}
          <section className="rx-dark relative">
            <div className="relative max-w-6xl mx-auto px-6 pt-24 pb-20 text-center flex flex-col items-center">
              <span className="rx-pill mb-7">
                <Sparkles className="h-3 w-3" />
                AI-powered resume toolkit
              </span>
              <h1 className="rx-display mb-8 max-w-4xl">
                Build a resume that gets you <span className="rx-accent">hired</span>
              </h1>
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed max-w-xl mx-auto mb-12">
                Create beautiful, ATS-friendly resumes with AI parsing, real-time analysis, job tailoring,
                and one-click PDF export. Deployed on Cloudflare Workers, D1, and Pages.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-14 w-full text-left">
                <button
                  onClick={() => setMode('editor')}
                  className="group rx-card flex items-center justify-center gap-3 bg-primary-600 text-white px-8 py-5 text-base font-semibold"
                >
                  <Plus className="h-5 w-5" />
                  Create New Resume
                  <ChevronRight className="h-5 w-5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
                </button>

                <div className="rx-card bg-white p-5 border">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="rounded-full bg-primary-100 p-2">
                      <Upload className="h-4 w-4 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 text-sm">Upload Existing Resume</h3>
                      <p className="text-xs text-slate-500">AI will parse and auto-fill your data</p>
                    </div>
                  </div>
                  <UploadZone
                    onParsed={(data) => {
                      resume.setResumeData(data);
                      setMode('editor');
                    }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <span className="rx-pill">
                  <Star className="h-3 w-3" />
                  25+ templates
                </span>
                <span className="rx-pill">
                  <Sparkles className="h-3 w-3" />
                  13 AI writing tools
                </span>
                <span className="rx-pill">
                  <ShieldCheck className="h-3 w-3" />
                  ATS-optimized output
                </span>
                <span className="rx-pill">
                  <Code2 className="h-3 w-3" />
                  Built on Cloudflare
                </span>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="rx-dark py-20 border-t">
            <div className="max-w-6xl mx-auto px-6">
              <div className="mb-14 max-w-2xl">
                <div className="rx-label mb-5">Capabilities</div>
                <h2 className="rx-h2 mb-5">Everything you need to stand out</h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  From AI analysis to one-click PDF export, every feature is designed to help you land more interviews.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {features.map((feature) => (
                  <div key={feature.title} className="rx-card p-6">
                    <div className="w-11 h-11 bg-primary-50 rounded-lg flex items-center justify-center mb-5 border border-primary-100">
                      <feature.icon className="h-5 w-5 text-primary-600" />
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2.5 tracking-tight">{feature.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* AI Tools */}
          <section className="rx-dark py-20 border-t">
            <div className="max-w-6xl mx-auto px-6">
              <div className="mb-14 max-w-2xl">
                <div className="rx-label mb-5">AI Toolkit</div>
                <h2 className="rx-h2 mb-5">AI-powered writing tools</h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Generate more than just a resume. Turn your experience into cover letters, LinkedIn content, interview prep, and more.
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {aiTools.map((tool) => (
                  <div
                    key={tool.label}
                    className="rx-card flex flex-col items-center text-center gap-3 p-5"
                  >
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center border">
                      <tool.icon className="h-4 w-4 text-primary-600" />
                    </div>
                    <span className="text-xs font-medium text-slate-800 leading-snug">{tool.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="rx-dark py-20 border-t">
            <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center">
              <div className="rx-label mb-5">Get Started</div>
              <h2 className="rx-h2 mb-6">Ready to build your resume?</h2>
              <p className="text-slate-600 text-sm mb-10 max-w-lg mx-auto leading-relaxed">
                No signup required. Start from scratch or upload an existing resume and let AI do the heavy lifting.
              </p>
              <button
                onClick={() => setMode('editor')}
                className="inline-flex items-center gap-2 bg-primary-600 text-white px-8 py-4 text-sm font-semibold"
              >
                <Plus className="h-4 w-4" />
                Start Building Free
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </section>

          {/* Footer */}
          <footer className="rx-dark rx-footer mt-auto" aria-label="Site footer">
            <div className="rx-footer-brand">
              <span className="rx-footer-mark" aria-hidden="true">F</span>
              <div>
                <strong>Free<span>App</span>Builder</strong>
                <div className="rx-dots" aria-hidden="true">
                  <i /><i /><i /><i /><i />
                </div>
              </div>
            </div>
            <div className="rx-footer-status">
              <i aria-hidden="true" />
              <span>AI Resume Builder operational</span>
            </div>
            <div className="rx-footer-copy">
              <span>
                Built by FreeAppBuilder
              </span>
              <span>
                &copy; {new Date().getFullYear()} FreeAppBuilder. All rights reserved.
              </span>
            </div>
          </footer>
        </div>
      </div>
    );
  }
  return (
    <div className="resume-app">
      <div className="app-shell h-screen flex flex-col">
      {/* Top Bar */}
      <header className="rx-dark rx-nav px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 shrink-0 relative z-30">
        <div className="flex items-center gap-3">
          <div className="rx-footer-mark" aria-hidden="true">
            <Layout className="h-4 w-4" />
          </div>
          <div>
            <h1 className="font-bold text-base text-slate-900 tracking-tight leading-none">
              Resume<span className="rx-accent">.</span>Builder
            </h1>
            <div className="rx-dots mt-1.5" aria-hidden="true">
              <i /><i /><i /><i /><i />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 min-w-0">
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Templates &amp; Settings</span>
            <span className="sm:hidden">Settings</span>
          </button>

          {/* Template Selector Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowTemplateDropdown((v) => !v)}
              aria-expanded={showTemplateDropdown}
              aria-haspopup="menu"
              className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors max-w-[50vw] sm:max-w-none"
            >
              <span className="truncate">
                <span className="hidden sm:inline">Template: </span>
                {templates.find((t) => t.key === resume.template)?.label || 'Modern'}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0" />
            </button>
            
            {showTemplateDropdown && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowTemplateDropdown(false)}
                />
                <div className="rx-dark absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto bg-white rounded-xl border border-slate-200 py-2 z-50 scrollbar-thin">
                  <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    Choose Template
                  </div>
                  {templates.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => {
                        resume.setTemplate(t.key);
                        setShowTemplateDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                        resume.template === t.key
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span>{t.label}</span>
                      {resume.template === t.key && (
                        <span className="text-primary-600">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <button
            onClick={() => setShowAiPanel((v) => !v)}
            aria-pressed={showAiPanel}
            className={`hidden lg:flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showAiPanel ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
            }`}
          >
            <Sparkles className="h-4 w-4" />
            AI
          </button>

          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Printer className="h-4 w-4" />
            {exporting ? 'Opening...' : 'Export PDF'}
          </button>
        </div>
      </header>

      {/*
        Pane switcher — only rendered below `lg`, where there is not enough
        width to show editor + preview side by side.
      */}
      <div className="rx-dark lg:hidden px-4 py-2 border-b shrink-0">
        <div className="rx-seg" role="tablist" aria-label="Editor panes">
          {editorPanes.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={pane === key}
              onClick={() => setPane(key)}
              className="rx-seg-btn"
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Split view (lg+) / single active pane (below lg) */}
      <div className="flex-1 flex overflow-hidden">
        <div
          className={`rx-dark flex-1 min-w-0 xl:min-w-[420px] border-r bg-white overflow-y-auto scrollbar-thin ${
            pane === 'editor' ? 'flex' : 'hidden'
          } lg:block`}
        >
          <Editor {...resume} />
        </div>

        <div
          className={`rx-preview-pane flex-1 min-w-0 xl:min-w-[360px] overflow-auto p-4 sm:p-6 lg:p-8 justify-center ${
            pane === 'preview' ? 'flex' : 'hidden'
          } lg:flex`}
          style={{ background: 'rgba(8, 8, 10, 0.55)' }}
        >
          <Preview data={resume.data} template={resume.template} settings={resume.settings} />
        </div>

        <div
          className={`rx-dark w-full lg:w-[340px] shrink-0 min-w-0 ${
            pane === 'ai' ? 'block' : 'hidden'
          } ${showAiPanel ? 'lg:block' : 'lg:hidden'}`}
        >
          <AiPanel
            data={resume.data}
            settings={resume.settings}
            onApplyTailor={resume.applyTailorSuggestions}
            onUpdateSummary={resume.updateSummary}
          />
        </div>
      </div>

      <SettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        data={resume.data}
        template={resume.template}
        setTemplate={resume.setTemplate}
        settings={resume.settings}
        updateSettings={resume.updateSettings}
        updateSectionOrder={resume.updateSectionOrder}
        setPhoto={resume.setPhoto}
      />
      </div>
    </div>
  );
}
